import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Languages, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useToast } from '@/hooks/useToast';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import {
  getCampaignLanguages,
  getAvailableLanguages,
  addCampaignLanguage,
  removeCampaignLanguage,
} from '@/lib/apis';
import type { CampaignLanguage, CampaignData } from '@/lib/types';

interface LanguageSectionProps {
  campaign: CampaignData;
  disabled?: boolean;
}

export function LanguageSection({ campaign, disabled = false }: LanguageSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [languageToRemove, setLanguageToRemove] = useState<CampaignLanguage | null>(null);

  const queryKey = ['campaign-languages', campaign.id];

  const { data } = useQuery({
    queryKey,
    queryFn: () => getCampaignLanguages(campaign.id),
    staleTime: 5 * 60 * 1000,
  });

  // The selectable catalog is served by the backend (enabled language_constant
  // rows) so the option list can never drift from what Google Ads accepts.
  const { data: catalog } = useQuery({
    queryKey: ['available-languages'],
    queryFn: getAvailableLanguages,
    staleTime: 60 * 60 * 1000,
  });

  const languages = data?.languages ?? [];

  const addMutation = useMutation({
    mutationFn: (code: string) => addCampaignLanguage(campaign.id, code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setIsAddOpen(false);
    },
    onError: () => {
      toast({ description: 'Failed to add language', variant: 'destructive' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (code: string) => removeCampaignLanguage(campaign.id, code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setLanguageToRemove(null);
    },
    onError: () => {
      toast({ description: 'Failed to remove language', variant: 'destructive' });
    },
  });

  const availableOptions = useMemo(() => {
    const existing = new Set(languages.map((l) => l.language_code));
    return (catalog?.languages ?? []).filter((o) => !existing.has(o.language_code));
  }, [languages, catalog]);

  const columns = useMemo(
    (): ColumnDef<CampaignLanguage>[] => [
      {
        accessorKey: 'name',
        header: 'Language',
        cell: ({ row }) => <span className='text-sm text-gray-900'>{row.original.name}</span>,
      },
      {
        id: 'actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex justify-end'>
            <IconButton
              variant='destructive'
              onClick={() => setLanguageToRemove(row.original)}
              disabled={disabled}
            >
              <Trash2 className='h-4 w-4' />
            </IconButton>
          </div>
        ),
        meta: { className: 'px-6 text-right' },
      },
    ],
    [disabled],
  );

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>Language</h3>
          <p className='text-sm text-gray-700 mt-1'>Target users who browse in specific languages.</p>
        </div>
        {!disabled && (
          <Button
            variant='outline'
            className='text-gray-900'
            onClick={() => setIsAddOpen(true)}
            disabled={availableOptions.length === 0}
          >
            <Plus className='h-4 w-4 mr-2' />
            Add Language
          </Button>
        )}
      </div>

      <DataTable
        data={languages}
        columns={columns}
        variant='bordered'
        pageSize={999}
        emptyIcon={Languages}
        emptyTitle='Targeting All Languages'
        emptyDescription='No language targeting is set, so ads can show in any language. Add a language to narrow targeting.'
      />

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        {/* Standard modal frame (#5814) — the old p-0 flush-list style was a one-off. */}
        <DialogContent className='sm:max-w-[500px] max-h-[85vh] flex flex-col' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Language</DialogTitle>
          </DialogHeader>
          <div className='py-2 flex-1 min-h-0 flex flex-col'>
            <Command className='rounded-md border flex-1 min-h-0 overflow-hidden'>
              <CommandInput placeholder='Search languages...' autoFocus />
              {/* The list is the flexible region: it shrinks with the modal (max-h-[85vh]
                  on DialogContent) instead of overflowing past the footer. */}
              <CommandList className='max-h-none flex-1 min-h-0 overflow-y-auto'>
                <CommandEmpty>No languages found.</CommandEmpty>
                <CommandGroup>
                  {availableOptions.map((opt) => (
                    <CommandItem
                      key={opt.language_code}
                      value={`${opt.name} ${opt.language_code}`}
                      disabled={addMutation.isPending}
                      onSelect={() => addMutation.mutate(opt.language_code)}
                      className='cursor-pointer'
                    >
                      {opt.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
            <p className='text-xs text-gray-500 mt-2'>Selecting a language adds it immediately.</p>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsAddOpen(false)} className='leading-none'>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={!!languageToRemove}
        onClose={() => setLanguageToRemove(null)}
        onConfirm={() => languageToRemove && removeMutation.mutate(languageToRemove.language_code)}
        title='Remove Language'
        description={`Remove "${languageToRemove?.name}" from language targeting?`}
        confirmText='Remove'
        cancelText='Cancel'
        isLoading={removeMutation.isPending}
      />
    </div>
  );
}
